import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card'
import { ArrowLeft, Buildings, CheckCircle } from '@phosphor-icons/react'
import { createTenant } from '../../services/tenants'

export default function NewTenantPage() {
  const navigate = useNavigate()
  const { showSuccess, showError } = useDialog()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  function validateForm() {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Nome do tenant é obrigatório'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setLoading(true)
      await createTenant(formData)

      // Show success message
      showSuccess('Sucesso', 'Tenant criado com sucesso!')

      // Navigate back to tenants list
      navigate('/master/tenants')
    } catch (error: any) {
      console.error('Error creating tenant:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao criar tenant')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/master/tenants')}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Novo Tenant</h1>
                <p className="text-sm text-gray-500">Criar nova organização no sistema</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Buildings size={20} className="text-emerald-600" weight="duotone" />
                  </div>
                  <div>
                    <CardTitle>Informações Básicas</CardTitle>
                    <CardDescription>
                      Configure os dados principais do tenant
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Tenant Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Tenant <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Empresa ABC Logística"
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Nome da organização que utilizará o sistema
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">O que acontece após criar o tenant?</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>Um novo ambiente isolado será criado para a organização</li>
                      <li>Você será automaticamente adicionado como administrador</li>
                      <li>Poderá criar empresas (shippers e freight forwarders) dentro do tenant</li>
                      <li>Poderá convidar usuários para participar</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/master/tenants')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Criando...' : 'Criar Tenant'}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </AppLayout>
  )
}
